import React from 'react';
import { Button, IconButton, Slider } from '@mui/material';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextSharpIcon from '@mui/icons-material/SkipNextSharp';
import SkipPreviousSharpIcon from '@mui/icons-material/SkipPreviousSharp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { formatClock } from './clipWindow';

export const SEEK_STEP = 3;

const styles = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: '0.2rem'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 4
    },
    button: {
        color: '#ffffff',
        backgroundColor: '#80808069'
    },
    rateButton: {
        width: '60px',
        color: '#ffffff'
    },
    scrubber: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '80%',
        maxWidth: 640,
        color: '#ffffff'
    },
    clock: {
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums',
        minWidth: 36,
        textAlign: 'center'
    }
};

// Shared control bar for both players on the /player/:data page: the clip viewer
// (TagVideo) and the full-game viewer (videoDialog). It holds no state of its
// own -- position, duration and the toggles all live in the parent.
export default function VideoControls({
    playing,
    muted,
    fullscreen,
    position,
    duration,
    onTogglePlay,
    onSeekBy,
    onScrub,
    onSlow,
    onFast,
    onToggleMute,
    onToggleFullscreen,
    showSkip = false,
    disablePrev = false,
    disableNext = false,
    onPrev,
    onNext
}) {
    const fraction = duration > 0 ? Math.min(100, Math.max(0, (position / duration) * 100)) : 0;

    return (
        <div style={styles.wrapper}>
            <div style={styles.row}>
                {showSkip && (
                    <IconButton aria-label="previous clip" onClick={onPrev} style={styles.button} disabled={disablePrev}>
                        <SkipPreviousSharpIcon />
                    </IconButton>
                )}
                <IconButton aria-label={`rewind ${SEEK_STEP} seconds`} onClick={() => onSeekBy(-SEEK_STEP)} style={styles.button}>
                    <FastRewindIcon />
                </IconButton>
                <Button variant="outlined" sx={styles.rateButton} onClick={onSlow}>
                    {'Slow'}
                </Button>
                <IconButton aria-label={playing ? 'pause' : 'play'} onClick={onTogglePlay} style={styles.button}>
                    {playing ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <Button variant="outlined" sx={styles.rateButton} onClick={onFast}>
                    {'Fast'}
                </Button>
                <IconButton aria-label={`forward ${SEEK_STEP} seconds`} onClick={() => onSeekBy(SEEK_STEP)} style={styles.button}>
                    <FastForwardIcon />
                </IconButton>
                {showSkip && (
                    <IconButton aria-label="next clip" onClick={onNext} style={styles.button} disabled={disableNext}>
                        <SkipNextSharpIcon />
                    </IconButton>
                )}
                <IconButton aria-label={muted ? 'unmute' : 'mute'} onClick={onToggleMute} style={styles.button}>
                    {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
                <IconButton aria-label={fullscreen ? 'exit fullscreen' : 'enter fullscreen'} onClick={onToggleFullscreen} style={styles.button}>
                    {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </div>
            <div style={styles.scrubber}>
                <span data-testid="video-elapsed" style={styles.clock}>{formatClock(position)}</span>
                <Slider
                    size="small"
                    aria-label="seek"
                    value={fraction}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => formatClock((value / 100) * duration)}
                    sx={{ color: 'white', padding: '0px' }}
                    onChange={(e, value) => onScrub((value / 100) * duration)}
                />
                <span data-testid="video-duration" style={styles.clock}>{formatClock(duration)}</span>
            </div>
        </div>
    );
}
